import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "preact/hooks";
import { Group, useQuery } from "./db.ts";
import { getApiBase } from "./config.ts";
import { t } from "./theme.ts";
import { isDarkMode } from "./routes.tsx";
import {
  ErrorMessage,
  formatLastActivity,
  GroupSelector,
  Loading,
  SelectInput,
} from "./uiComponents.tsx";
import { ClipLoader } from "react-spinners";
import { Edit2, Play, Plus, RefreshCw, Trash2 } from "lucide-preact";
import { empty } from "@uri/gamla";
import { marked } from "marked";
import { extractErrorMessage } from "./notify.ts";

const apiBase = getApiBase();

const callApi = (
  endpoint: string,
  token: string,
  payload: Record<string, unknown> = {},
) =>
  fetch(apiBase, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ endpoint, token, payload }),
  }).then((r) => r.json());

const ChevronDown = ({ size = 16, color = "currentColor", style = {} }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transition: "transform 0.15s ease",
      display: "inline-block",
      ...style,
    }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ChevronRight = ({ size = 16, color = "currentColor", style = {} }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transition: "transform 0.15s ease",
      display: "inline-block",
      ...style,
    }}
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const ChevronUp = ({ size = 16, color = "currentColor", style = {} }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transition: "transform 0.15s ease",
      display: "inline-block",
      ...style,
    }}
  >
    <polyline points="18 15 12 9 6 15" />
  </svg>
);

const ClipboardCheckIcon = ({ size = 24, color = "currentColor" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "inline-block", verticalAlign: "middle" }}
  >
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
    <polyline points="9 11 12 14 17 9" />
  </svg>
);

type Checkup = {
  id: string;
  interests: string;
  sampleSize: number;
  frequencyDays: number;
  lastRunAt?: number;
  runningAt?: number;
  group: { id: string; name: string }[];
  excludeGroups?: { id: string; name: string }[];
  onlyStartedInPeriod?: boolean;
  githubToken?: string;
  githubRepo?: string;
};

type Report = {
  id: string;
  content: string;
  createdAt: number;
  conversationIds: string;
};

const lockTimeout = 10 * 60 * 1000;

const isRunning = (checkup: Checkup) =>
  !!checkup.runningAt && Date.now() - checkup.runningAt < lockTimeout;

const reportMarkdownCss = (th: ReturnType<typeof t>) => `
.report-markdown {unicode-bidi:plaintext;text-align:start}
.report-markdown h1,.report-markdown h2,.report-markdown h3,.report-markdown h4{margin:20px 0 8px;font-weight:700;color:${th.textBright};unicode-bidi:plaintext;text-align:start}
.report-markdown h1{font-size:1.6em}
.report-markdown h2{font-size:1.4em}
.report-markdown h3{font-size:1.2em}
.report-markdown p{margin:8px 0;unicode-bidi:plaintext;text-align:start}
.report-markdown ul,.report-markdown ol{margin:8px 0;padding-left:24px}
.report-markdown li{margin:4px 0;unicode-bidi:plaintext;text-align:start}
.report-markdown li>ul,.report-markdown li>ol{margin:4px 0}
.report-markdown hr{border:none;border-top:1px solid ${th.border};margin:16px 0}
.report-markdown a{color:${th.primary};text-decoration:underline}
.report-markdown a:hover{opacity:0.8}
.report-markdown code{background:${th.inputBg};padding:1px 5px;border-radius:3px;font-size:0.9em}
.report-markdown pre{background:${th.inputBg};padding:12px;border-radius:6px;overflow-x:auto;margin:8px 0;direction:ltr;text-align:left;unicode-bidi:isolate}
.report-markdown pre code{background:none;padding:0}
.report-markdown blockquote{border-left:3px solid ${th.border};margin:8px 0;padding:4px 12px;color:${th.textMuted};unicode-bidi:plaintext;text-align:start}
`;

const playBipBeep = () => {
  const ctx = new AudioContext();
  const playTone = (freq: number, startAt: number) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.15, startAt);
    gain.gain.exponentialRampToValueAtTime(0.001, startAt + 0.1);
    osc.start(startAt);
    osc.stop(startAt + 0.1);
  };
  playTone(880, ctx.currentTime);
  playTone(1100, ctx.currentTime + 0.12);
};

const CheckupCard = (
  { checkup, accountSecret, reports, onUpdate, groups }: {
    checkup: Checkup;
    accountSecret: string;
    reports: Report[];
    onUpdate: () => void;
    groups: { id: string; name: string; lastActivityAt?: number }[];
  },
) => {
  const th = t();
  const cardRef = useRef<HTMLDivElement>(null);
  const urlParams = typeof location !== "undefined"
    ? new URLSearchParams(globalThis.location?.search)
    : null;
  const targetCheckupId = urlParams?.get("checkupId") ||
    urlParams?.get("checkup");
  const isTarget = targetCheckupId === checkup.id;

  const [editing, setEditing] = useState(isTarget);
  const [expanded, setExpanded] = useState(isTarget);

  useEffect(() => {
    if (isTarget && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isTarget]);
  const [groupId, setGroupId] = useState(checkup.group?.[0]?.id ?? "");
  const [interests, setInterests] = useState(checkup.interests);
  const [sampleSize, setSampleSize] = useState(checkup.sampleSize);
  const [frequencyDays, setFrequencyDays] = useState(checkup.frequencyDays);
  const [excludeGroupIds, setExcludeGroupIds] = useState<string[]>(
    checkup.excludeGroups?.map((g) => g.id) || [],
  );
  const [onlyStartedInPeriod, setOnlyStartedInPeriod] = useState(
    checkup.onlyStartedInPeriod ?? false,
  );
  const [githubToken, setGithubToken] = useState("");
  const [githubRepo, setGithubRepo] = useState(checkup.githubRepo ?? "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [runningLocal, setRunningLocal] = useState(false);
  const [expandedReportId, setExpandedReportId] = useState<string | null>(null);
  const [showAllReports, setShowAllReports] = useState(false);

  const groupName = checkup.group?.[0]?.name ?? "Unknown group";
  const running = runningLocal || isRunning(checkup);

  const handleRun = () => {
    setError("");
    setRunningLocal(true);
    callApi("runCheckup", accountSecret, { checkupId: checkup.id })
      .then((res: { ok?: boolean; error?: string }) => {
        if (res.ok) onUpdate();
        else setError(res.error || "Failed to run");
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setRunningLocal(false));
  };

  const handleSave = () => {
    setSaving(true);
    setError("");
    callApi("updateCheckup", accountSecret, {
      checkupId: checkup.id,
      groupId,
      interests,
      sampleSize,
      frequencyDays,
      excludeGroupIds,
      onlyStartedInPeriod,
      githubToken: githubToken ? githubToken : undefined,
      githubRepo: githubRepo.trim() ? githubRepo.trim() : undefined,
    })
      .then((res: { ok?: boolean; error?: string }) => {
        if (res.ok) {
          setEditing(false);
          onUpdate();
        } else setError(res.error || "Failed to update");
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setSaving(false));
  };

  const handleDelete = () => {
    if (!confirm("Delete this checkup?")) return;
    callApi("deleteCheckup", accountSecret, { checkupId: checkup.id })
      .then((res: { ok?: boolean; error?: string }) => {
        if (res.ok) onUpdate();
        else setError(res.error || "Failed to delete");
      })
      .catch((e: Error) => setError(e.message));
  };

  const linkStyle = {
    background: "transparent",
    border: "none",
    color: th.textDim,
    fontSize: 12,
    cursor: "pointer",
    padding: "2px 0",
    textDecoration: "underline" as const,
    transition: "color 0.15s",
  };

  return (
    <div
      ref={cardRef}
      style={{
        background: th.cardBg,
        borderRadius: 8,
        padding: 16,
        border: `1px solid ${editing ? th.primary : th.border}`,
        marginBottom: 8,
        transition: "border-color 0.15s",
      }}
    >
      {/* Header: group name + last run */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: editing ? "default" : "pointer",
          userSelect: "none",
        }}
        onClick={() => {
          if (!editing) setExpanded(!expanded);
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {!editing && (
            expanded
              ? <ChevronDown size={14} color={th.textFaint} />
              : <ChevronRight size={14} color={th.textFaint} />
          )}
          <span style={{ fontWeight: 600, color: th.textBright, fontSize: 15 }}>
            {groupName}
          </span>
        </div>
        <span style={{ fontSize: 12, color: th.textFaint }}>
          {checkup.lastRunAt
            ? `Last run ${formatLastActivity(checkup.lastRunAt)}`
            : "Never run"}
        </span>
      </div>

      {(expanded || editing) && (
        <div style={{ marginTop: 16 }}>
          {editing
            ? (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                <GroupSelector
                  groups={groups}
                  value={groupId}
                  onChange={setGroupId}
                />
                <label
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  <span style={{ fontSize: 12, color: th.textDim }}>
                    Interests
                  </span>
                  <textarea
                    value={interests}
                    onInput={(e) => setInterests(e.currentTarget.value)}
                    className="input-field"
                    style={{
                      minHeight: 100,
                      resize: "vertical",
                      marginBottom: 0,
                    }}
                    placeholder="Describe what you care about..."
                  />
                </label>
                <div style={{ display: "flex", gap: 12 }}>
                  <label
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                      flex: 1,
                    }}
                  >
                    <span style={{ fontSize: 12, color: th.textDim }}>
                      Sample size
                    </span>
                    <input
                      type="number"
                      min={1}
                      value={sampleSize}
                      onInput={(e) =>
                        setSampleSize(
                          parseInt(e.currentTarget.value) || sampleSize,
                        )}
                      className="input-field"
                      style={{ marginBottom: 0 }}
                    />
                  </label>
                  <label
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                      flex: 1,
                    }}
                  >
                    <span style={{ fontSize: 12, color: th.textDim }}>
                      Every N days
                    </span>
                    <input
                      type="number"
                      min={1}
                      value={frequencyDays}
                      onInput={(e) =>
                        setFrequencyDays(
                          parseInt(e.currentTarget.value) || frequencyDays,
                        )}
                      className="input-field"
                      style={{ marginBottom: 0 }}
                    />
                  </label>
                </div>
                {groups.filter((g) => g.id !== groupId).length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                      marginTop: 8,
                    }}
                  >
                    <span style={{ fontSize: 12, color: th.textDim }}>
                      Exclude Groups
                    </span>
                    <SelectInput
                      name=""
                      value=""
                      onChange={(selectedId) => {
                        if (
                          selectedId && !excludeGroupIds.includes(selectedId)
                        ) {
                          setExcludeGroupIds([...excludeGroupIds, selectedId]);
                        }
                      }}
                      options={[
                        { id: "", name: "Select group to exclude..." },
                        ...[...groups]
                          .filter((g) =>
                            g.id !== groupId && !excludeGroupIds.includes(g.id)
                          )
                          .sort(
                            (a, b) =>
                              (b.lastActivityAt ?? 0) - (a.lastActivityAt ?? 0),
                          )
                          .map((g) => ({
                            id: g.id,
                            name: `${g.name} (${
                              formatLastActivity(g.lastActivityAt)
                            })`,
                          })),
                      ]}
                    />
                    {excludeGroupIds.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 6,
                          marginTop: 4,
                        }}
                      >
                        {excludeGroupIds.map((id) => {
                          const name = groups.find((g) => g.id === id)?.name ||
                            "Unknown";
                          return (
                            <div
                              key={id}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                background: th.cardBgAccent,
                                border: `1px solid ${th.border}`,
                                borderRadius: 16,
                                padding: "4px 10px",
                                fontSize: 12,
                                color: th.text,
                              }}
                            >
                              <span>{name}</span>
                              <button
                                type="button"
                                onClick={() =>
                                  setExcludeGroupIds(
                                    excludeGroupIds.filter((x) => x !== id),
                                  )}
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  color: th.danger,
                                  cursor: "pointer",
                                  fontWeight: "bold",
                                  padding: 0,
                                  fontSize: 12,
                                }}
                              >
                                &times;
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    color: th.textDim,
                    fontSize: 13,
                    cursor: "pointer",
                    marginTop: 8,
                    marginBottom: 8,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={onlyStartedInPeriod}
                    onChange={(e) => {
                      setOnlyStartedInPeriod(e.currentTarget.checked);
                    }}
                    style={{ cursor: "pointer" }}
                  />
                  Only scan conversations that started in this period
                </label>
                <label
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  <span style={{ fontSize: 12, color: th.textDim }}>
                    GitHub Personal Access Token{" "}
                    {checkup.githubToken
                      ? "(configured — enter to replace)"
                      : "(optional, enter to update)"}
                  </span>
                  <input
                    type="password"
                    value={githubToken}
                    onInput={(e) => setGithubToken(e.currentTarget.value)}
                    className="input-field"
                    style={{ marginBottom: 0 }}
                    placeholder={checkup.githubToken
                      ? "•••••••• (configured)"
                      : "ghp_..."}
                  />
                  <span
                    style={{
                      fontSize: 10,
                      color: th.textFaint,
                      lineHeight: 1.4,
                      marginTop: 2,
                    }}
                  >
                    ⚠️ Recommendation: Configure a fine-grained token with{" "}
                    <strong>Read-only (Metadata)</strong> and{" "}
                    <strong>Read/Write (Issues)</strong>{" "}
                    permissions only. Since Vivi has web-access capabilities,
                    she could theoretically leak information or be exposed to
                    prompt injection from analyzed chat logs.
                  </span>
                </label>
                <label
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  <span style={{ fontSize: 12, color: th.textDim }}>
                    GitHub Repository (optional, e.g. owner/repo)
                  </span>
                  <input
                    type="text"
                    value={githubRepo}
                    onInput={(e) => setGithubRepo(e.currentTarget.value)}
                    className="input-field"
                    style={{ marginBottom: 0 }}
                    placeholder="owner/repo"
                  />
                </label>
                {error && (
                  <div style={{ color: th.danger, fontSize: 13 }}>{error}</div>
                )}
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    style={{
                      background: th.primary,
                      color: isDarkMode.value ? "#121212" : "#fff",
                      border: "none",
                      borderRadius: 6,
                      padding: "8px 20px",
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: saving ? "not-allowed" : "pointer",
                      opacity: saving ? 0.6 : 1,
                      transition: "opacity 0.15s",
                    }}
                  >
                    {saving ? "Saving..." : "Save"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(false);
                      setGroupId(checkup.group?.[0]?.id ?? "");
                      setInterests(checkup.interests);
                      setSampleSize(checkup.sampleSize);
                      setFrequencyDays(checkup.frequencyDays);
                      setExcludeGroupIds(
                        checkup.excludeGroups?.map((g) => g.id) || [],
                      );
                      setOnlyStartedInPeriod(
                        checkup.onlyStartedInPeriod ?? false,
                      );
                      setGithubToken("");
                      setGithubRepo(checkup.githubRepo ?? "");
                      setError("");
                    }}
                    style={linkStyle}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )
            : (
              <>
                <div
                  style={{ fontSize: 13, color: th.textMuted, marginBottom: 4 }}
                >
                  {checkup.interests || "No specific interests set"}
                </div>
                {checkup.excludeGroups && checkup.excludeGroups.length > 0 && (
                  <div
                    style={{
                      fontSize: 11,
                      color: th.textFaint,
                      marginBottom: 8,
                    }}
                  >
                    Excluding: {checkup.excludeGroups.map((g) =>
                      g.name
                    ).join(", ")}
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ fontSize: 12, color: th.textFaint }}>
                    Sample: {checkup.sampleSize} &middot; Every{" "}
                    {checkup.frequencyDays}d
                    {checkup.onlyStartedInPeriod && (
                      <>&middot; Only started in period</>
                    )}
                    {checkup.githubToken && (
                      <>&middot; GitHub: {checkup.githubRepo ? `${checkup.githubRepo} (configured)` : "token configured"}</>
                    )}
                  </span>
                  <div
                    style={{ display: "flex", gap: 10, alignItems: "center" }}
                  >
                    <button
                      type="button"
                      onClick={handleRun}
                      disabled={running}
                      style={{
                        ...linkStyle,
                        color: running ? th.textFaint : th.primary,
                        textDecoration: running ? "none" : "underline",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      {running
                        ? (
                          <>
                            <ClipLoader size={10} color={th.textDim} /> Running
                          </>
                        )
                        : (
                          <>
                            <Play size={12} /> Run now
                          </>
                        )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditing(true)}
                      style={{
                        ...linkStyle,
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <Edit2 size={12} /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={handleDelete}
                      style={{
                        ...linkStyle,
                        color: th.danger,
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
                {error && (
                  <div style={{ color: th.danger, fontSize: 13, marginTop: 6 }}>
                    {error}
                  </div>
                )}
              </>
            )}

          {reports.length > 0 && (
            <div
              style={{
                marginTop: 12,
                borderTop: `1px solid ${th.border}`,
                paddingTop: 8,
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  color: th.textDim,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  marginBottom: 6,
                }}
              >
                Reports ({reports.length})
              </div>
              {(showAllReports ? reports : reports.slice(0, 5)).map(
                (report) => {
                  const date = new Date(report.createdAt);
                  const convCount = (() => {
                    try {
                      return JSON.parse(report.conversationIds).length;
                    } catch {
                      return 0;
                    }
                  })();
                  const expanded = expandedReportId === report.id;
                  return (
                    <div
                      key={report.id}
                      style={{
                        borderRadius: 6,
                        border: `1px solid ${th.border}`,
                        marginBottom: 6,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        onClick={() =>
                          setExpandedReportId(expanded ? null : report.id)}
                        style={{
                          padding: "8px 12px",
                          cursor: "pointer",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          fontSize: 13,
                        }}
                      >
                        <span style={{ color: th.textMuted }}>
                          {date.toLocaleDateString()}{" "}
                          {date.toLocaleTimeString()}
                        </span>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <span style={{ color: th.textDim, fontSize: 12 }}>
                            {convCount} conv{convCount !== 1 ? "s" : ""}
                          </span>
                          {expanded
                            ? <ChevronUp size={12} color={th.textFaint} />
                            : <ChevronDown size={12} color={th.textFaint} />}
                        </div>
                      </div>
                      {expanded && (
                        <div
                          style={{
                            padding: "0 12px 12px 12px",
                            borderTop: `1px solid ${th.border}`,
                          }}
                        >
                          <style
                            dangerouslySetInnerHTML={{
                              __html: reportMarkdownCss(th),
                            }}
                          />
                          <div
                            className="report-markdown"
                            style={{
                              fontSize: 15,
                              color: th.text,
                              lineHeight: 1.6,
                              paddingTop: 12,
                            }}
                            dangerouslySetInnerHTML={{
                              __html: marked(report.content) as string,
                            }}
                          />
                        </div>
                      )}
                    </div>
                  );
                },
              )}
              {reports.length > 5 && (
                <button
                  type="button"
                  onClick={() => setShowAllReports((v) => !v)}
                  style={{
                    ...linkStyle,
                    color: th.primary,
                    marginTop: 4,
                  }}
                >
                  {showAllReports
                    ? "Show less"
                    : `Load more (${reports.length - 5} older)`}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CreateCheckupForm = (
  { accountSecret, groups, onCreated, onCancel }: {
    accountSecret: string;
    groups: { id: string; name: string; lastActivityAt?: number }[];
    onCreated: () => void;
    onCancel?: () => void;
  },
) => {
  const th = t();
  const [groupId, setGroupId] = useState("");
  const [interests, setInterests] = useState("");
  const [sampleSize, setSampleSize] = useState(10);
  const [frequencyDays, setFrequencyDays] = useState(7);
  const [excludeGroupIds, setExcludeGroupIds] = useState<string[]>([]);
  const [onlyStartedInPeriod, setOnlyStartedInPeriod] = useState(false);
  const [githubToken, setGithubToken] = useState("");
  const [githubRepo, setGithubRepo] = useState("");
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);

  const handleCreate = () => {
    if (!groupId) {
      setError("Select a group");
      return;
    }
    setCreating(true);
    setError("");
    callApi("createCheckup", accountSecret, {
      groupId,
      interests,
      sampleSize,
      frequencyDays,
      excludeGroupIds: excludeGroupIds.length > 0 ? excludeGroupIds : undefined,
      onlyStartedInPeriod,
      githubToken: githubToken ? githubToken : undefined,
      githubRepo: githubRepo.trim() ? githubRepo.trim() : undefined,
    })
      .then((res: { ok?: boolean; error?: string }) => {
        if (res.ok) {
          setGroupId("");
          setInterests("");
          setSampleSize(10);
          setFrequencyDays(7);
          setExcludeGroupIds([]);
          setOnlyStartedInPeriod(false);
          setGithubToken("");
          setGithubRepo("");
          onCreated();
        } else setError(res.error || "Failed to create");
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setCreating(false));
  };

  const otherGroups = groups.filter((g) => g.id !== groupId);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <GroupSelector
        groups={groups}
        value={groupId}
        onChange={setGroupId}
      />

      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, color: th.textDim }}>
          Interests (optional)
        </span>
        <textarea
          value={interests}
          onInput={(e) => setInterests(e.currentTarget.value)}
          className="input-field"
          style={{ minHeight: 60, resize: "vertical", marginBottom: 0 }}
          placeholder="Describe what you care about in these conversations..."
        />
      </label>

      <div style={{ display: "flex", gap: 12 }}>
        <label
          style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}
        >
          <span style={{ fontSize: 12, color: th.textDim }}>Sample size</span>
          <input
            type="number"
            min={1}
            value={sampleSize}
            onInput={(e) =>
              setSampleSize(parseInt(e.currentTarget.value) || sampleSize)}
            className="input-field"
            style={{ marginBottom: 0 }}
          />
        </label>
        <label
          style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}
        >
          <span style={{ fontSize: 12, color: th.textDim }}>Every N days</span>
          <input
            type="number"
            min={1}
            value={frequencyDays}
            onInput={(e) =>
              setFrequencyDays(
                parseInt(e.currentTarget.value) || frequencyDays,
              )}
            className="input-field"
            style={{ marginBottom: 0 }}
          />
        </label>
      </div>

      {otherGroups.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 12, color: th.textDim }}>
            Exclude Groups
          </span>
          <SelectInput
            name=""
            value=""
            onChange={(selectedId) => {
              if (selectedId && !excludeGroupIds.includes(selectedId)) {
                setExcludeGroupIds([...excludeGroupIds, selectedId]);
              }
            }}
            options={[
              { id: "", name: "Select group to exclude..." },
              ...[...otherGroups]
                .filter((g) =>
                  !excludeGroupIds.includes(g.id)
                )
                .sort(
                  (a, b) => (b.lastActivityAt ?? 0) - (a.lastActivityAt ?? 0),
                )
                .map((g) => ({
                  id: g.id,
                  name: `${g.name} (${formatLastActivity(g.lastActivityAt)})`,
                })),
            ]}
          />
          {excludeGroupIds.length > 0 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                marginTop: 4,
              }}
            >
              {excludeGroupIds.map((id) => {
                const name = groups.find((g) => g.id === id)?.name || "Unknown";
                return (
                  <div
                    key={id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      background: th.cardBgAccent,
                      border: `1px solid ${th.border}`,
                      borderRadius: 16,
                      padding: "4px 10px",
                      fontSize: 12,
                      color: th.text,
                    }}
                  >
                    <span>{name}</span>
                    <button
                      type="button"
                      onClick={() =>
                        setExcludeGroupIds(
                          excludeGroupIds.filter((x) => x !== id),
                        )}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: th.danger,
                        cursor: "pointer",
                        fontWeight: "bold",
                        padding: 0,
                        fontSize: 12,
                      }}
                    >
                      &times;
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          color: th.textDim,
          fontSize: 13,
          cursor: "pointer",
          marginTop: 4,
          marginBottom: 4,
        }}
      >
        <input
          type="checkbox"
          checked={onlyStartedInPeriod}
          onChange={(e) => {
            setOnlyStartedInPeriod(e.currentTarget.checked);
          }}
          style={{ cursor: "pointer" }}
        />
        Only scan conversations that started in this period
      </label>

      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, color: th.textDim }}>
          GitHub Personal Access Token (optional)
        </span>
        <input
          type="password"
          value={githubToken}
          onInput={(e) => setGithubToken(e.currentTarget.value)}
          className="input-field"
          style={{ marginBottom: 0 }}
          placeholder="ghp_..."
        />
        <span
          style={{
            fontSize: 10,
            color: th.textFaint,
            lineHeight: 1.4,
            marginTop: 2,
          }}
        >
          ⚠️ Recommendation: Configure a fine-grained token with{" "}
          <strong>Read-only (Metadata)</strong> and{" "}
          <strong>Read/Write (Issues)</strong>{" "}
          permissions only. Since Vivi has web-access capabilities, she could
          theoretically leak information or be exposed to prompt injection from
          analyzed chat logs.
        </span>
      </label>

      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, color: th.textDim }}>
          GitHub Repository (optional, e.g. owner/repo)
        </span>
        <input
          type="text"
          value={githubRepo}
          onInput={(e) => setGithubRepo(e.currentTarget.value)}
          className="input-field"
          style={{ marginBottom: 0 }}
          placeholder="owner/repo"
        />
      </label>

      {error && <div style={{ color: th.danger, fontSize: 13 }}>{error}</div>}

      <div
        style={{ display: "flex", gap: 12, marginTop: 4, alignItems: "center" }}
      >
        <button
          type="button"
          className="compact-button"
          onClick={handleCreate}
          disabled={creating}
        >
          {creating ? "Creating..." : "Create Checkup"}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            style={{
              background: "transparent",
              border: "none",
              color: th.textDim,
              cursor: "pointer",
              textDecoration: "underline",
              fontSize: 13,
            }}
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

export const CheckupsPage = () => {
  const th = t();

  const [retrying, setRetrying] = useState(false);
  const handleRetry = useCallback(() => {
    setRetrying(true);
    setTimeout(() => setRetrying(false), 50);
  }, []);

  const { isLoading, error, data } = useQuery(
    retrying ? null : {
      accounts: { checkups: { group: {}, excludeGroups: {} } },
    },
  );

  const { data: groupsData } = useQuery({
    groups: {
      $: {
        limit: 200,
        order: { lastActivityAt: "desc" },
      },
    },
  });

  const hasAutoRetriedRef = useRef(false);
  useEffect(() => {
    if (error) {
      const msg = extractErrorMessage(error);
      const isTimeout = msg.includes("timed out") || msg.includes("timeout");
      if (isTimeout && !hasAutoRetriedRef.current) {
        hasAutoRetriedRef.current = true;
        const timer = setTimeout(() => {
          handleRetry();
        }, 1500);
        return () => clearTimeout(timer);
      }
    } else {
      hasAutoRetriedRef.current = false;
    }
  }, [error, handleRetry]);

  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const [reports, setReports] = useState<Record<string, Report[]>>({});
  const knownReportIdsRef = useRef<Set<string> | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);

  const accounts = data?.accounts || [];
  const accountSecret = accounts[0]?.secret;

  const availableGroups = useMemo(() => {
    const groupMap = new Map<
      string,
      { id: string; name: string; lastActivityAt?: number }
    >();
    for (const g of (groupsData?.groups as Group[] | undefined) || []) {
      groupMap.set(g.id, {
        id: g.id,
        name: g.name,
        lastActivityAt: g.lastActivityAt,
      });
    }
    for (const account of accounts) {
      // deno-lint-ignore no-explicit-any
      for (const c of (account as any).checkups || []) {
        const cg = Array.isArray(c.group) ? c.group[0] : c.group;
        if (cg && !groupMap.has(cg.id)) {
          groupMap.set(cg.id, {
            id: cg.id,
            name: cg.name,
            lastActivityAt: cg.lastActivityAt,
          });
        }
        for (const eg of c.excludeGroups || []) {
          if (eg && !groupMap.has(eg.id)) {
            groupMap.set(eg.id, {
              id: eg.id,
              name: eg.name,
              lastActivityAt: eg.lastActivityAt,
            });
          }
        }
      }
    }
    return Array.from(groupMap.values()).sort(
      (a, b) => (b.lastActivityAt ?? 0) - (a.lastActivityAt ?? 0),
    );
  }, [groupsData, accounts]);

  useEffect(() => {
    if (!accountSecret) return;
    callApi("listReports", accountSecret, { limit: 100 })
      .then(
        (
          res: {
            ok?: boolean;
            reports?: (Report & { checkup?: { id: string } })[];
            error?: string;
          },
        ) => {
          if (!res.ok || !res.reports) return;
          const incomingIds = new Set(res.reports.map((r) => r.id));
          const prev = knownReportIdsRef.current;
          if (prev !== null && document.hasFocus()) {
            for (const id of incomingIds) {
              if (!prev.has(id)) {
                playBipBeep();
                break;
              }
            }
          }
          knownReportIdsRef.current = incomingIds;
          const grouped: Record<string, Report[]> = {};
          for (const r of res.reports) {
            const cid = r.checkup?.id;
            if (!cid) continue;
            (grouped[cid] ??= []).push(r);
          }
          setReports(grouped);
        },
      )
      .catch(() => {});
  }, [accountSecret, refreshKey]);

  // Auto-poll for new reports every 30 seconds
  useEffect(() => {
    if (!accountSecret) return;
    const interval = setInterval(refresh, 30_000);
    return () => clearInterval(interval);
  }, [accountSecret, refresh]);

  if (isLoading) return <Loading text="Loading checkups" fullPage />;
  if (error) {
    return (
      <ErrorMessage
        error={error}
        action={
          <button
            type="button"
            className="compact-button"
            style={{
              fontSize: 13,
              padding: "6px 16px",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontWeight: 600,
              cursor: "pointer",
              marginTop: 4,
            }}
            onClick={handleRetry}
          >
            <RefreshCw size={14} />
            Retry
          </button>
        }
      />
    );
  }

  if (empty(accounts)) {
    return (
      <div
        style={{
          padding: 24,
          textAlign: "center",
          color: th.textDim,
        }}
      >
        No accounts found. Create one in Accounts first.
      </div>
    );
  }

  return (
    <div
      style={{
        padding: 24,
        background: th.pageBg,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 24,
        color: th.text,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          maxWidth: 600,
          width: "100%",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <ClipboardCheckIcon size={24} color={th.primary} />
          <h1
            style={{
              fontSize: 24,
              fontWeight: 600,
              color: th.textBright,
              margin: 0,
            }}
          >
            Checkups
          </h1>
        </div>
        <button
          type="button"
          onClick={() => setShowCreateModal(true)}
          style={{
            background: th.primary,
            color: isDarkMode.value ? "#121212" : "#ffffff",
            border: "none",
            borderRadius: 6,
            padding: "8px 16px",
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Plus size={16} /> New Checkup
        </button>
      </div>

      {accounts.map((account) => {
        // deno-lint-ignore no-explicit-any
        const accountCheckups = (account as any).checkups || [];

        return (
          <div
            key={account.id}
            style={{ maxWidth: 600, width: "100%" }}
          >
            {accountCheckups.length > 0
              ? (
                <div style={{ marginBottom: 16 }}>
                  {accountCheckups.map(
                    // deno-lint-ignore no-explicit-any
                    (checkup: any) => {
                      const groupObj = Array.isArray(checkup.group)
                        ? checkup.group[0]
                        : checkup.group;
                      const groupName = groupObj?.name ?? "Unknown group";
                      return (
                        <CheckupCard
                          key={checkup.id}
                          checkup={{
                            ...checkup,
                            group: groupObj
                              ? [{ id: groupObj.id, name: groupName }]
                              : (checkup.groupId
                                ? [{ id: checkup.groupId, name: groupName }]
                                : []),
                            excludeGroups: checkup.excludeGroups || [],
                          }}
                          accountSecret={account.secret}
                          reports={reports[checkup.id] || []}
                          onUpdate={refresh}
                          groups={availableGroups}
                        />
                      );
                    },
                  )}
                </div>
              )
              : (
                <div
                  style={{
                    padding: "40px 20px",
                    textAlign: "center",
                    background: th.cardBg,
                    border: `1px solid ${th.border}`,
                    borderRadius: 8,
                    color: th.textMuted,
                    fontSize: 14,
                  }}
                >
                  No checkups created yet. Click "+ New Checkup" to set one up.
                </div>
              )}

            {showCreateModal && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(0, 0, 0, 0.55)",
                  backdropFilter: "blur(4px)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 2000,
                  padding: 16,
                }}
                onClick={() => setShowCreateModal(false)}
              >
                <div
                  style={{
                    background: th.cardBg,
                    borderRadius: 12,
                    padding: 24,
                    border: `1px solid ${th.border}`,
                    width: "100%",
                    maxWidth: 500,
                    boxShadow: "0 10px 25px -5px rgba(0,0,0,0.4)",
                    position: "relative",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 20,
                    }}
                  >
                    <h2
                      style={{
                        fontSize: 18,
                        fontWeight: 600,
                        color: th.textBright,
                        margin: 0,
                      }}
                    >
                      New Checkup
                    </h2>
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: th.textDim,
                        fontSize: 20,
                        cursor: "pointer",
                        padding: 0,
                        lineHeight: 1,
                      }}
                    >
                      &times;
                    </button>
                  </div>

                  <CreateCheckupForm
                    accountSecret={account.secret}
                    groups={availableGroups}
                    onCreated={() => {
                      setShowCreateModal(false);
                      refresh();
                    }}
                    onCancel={() => setShowCreateModal(false)}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
